import React, { useState, useEffect, useRef } from 'react';
import Icon from './Icon';
import Button from './Button';
import Input from './Input';

const loadStripeSdk = () => {
  return new Promise((resolve) => {
    if (window.Stripe) {
      resolve(window.Stripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim()));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      resolve(window.Stripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim()));
    };
    document.head.appendChild(script);
  });
};

export default function StripePaymentForm({ 
  onSuccess, 
  onCancel, 
  buttonText = "Save Payment Method",
  userEmail = "",
  defaultNickname = "",
  disabled = false,
  children
}) {
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  
  const [stripeReady, setStripeReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detectedBrand, setDetectedBrand] = useState('Card');
  
  const [cardHolderName, setCardHolderName] = useState('');
  const [nickname, setNickname] = useState(defaultNickname);

  useEffect(() => {
    let mounted = true;
    loadStripeSdk().then(stripe => {
      if (!mounted) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      elementsRef.current = elements;

      const style = {
          base: {
              color: '#1E293B',
              fontFamily: '"Inter", sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '14px',
              '::placeholder': {
                  color: '#64748B'
              }
          },
          invalid: {
              color: '#EF4444',
              iconColor: '#EF4444'
          }
      };

      const cardNumber = elements.create('cardNumber', { style, showIcon: true });
      cardNumber.mount('#card-number-element');
      cardNumber.on('change', (e) => {
          if (e.brand) setDetectedBrand(e.brand.charAt(0).toUpperCase() + e.brand.slice(1));
      });

      const cardExpiry = elements.create('cardExpiry', { style });
      cardExpiry.mount('#card-expiry-element');

      const cardCvc = elements.create('cardCvc', { style });
      cardCvc.mount('#card-cvc-element');

      setStripeReady(true);
    });

    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardHolderName) {
      setError('Please enter Cardholder Name');
      return;
    }

    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) {
      setError('Payment system not initialized. Please try refreshing.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cardElement = elements.getElement('cardNumber');
      
      // Tokenize
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
              name: cardHolderName,
              email: userEmail
          },
      });

      if (stripeError) {
          throw new Error(stripeError.message);
      }

      // Success
      const brand = paymentMethod.card.brand;
      const capitalizedBrand = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';

      const paymentData = {
          cardHolderName,
          nickname,
          cardLastFour: paymentMethod.card.last4,
          cardBrand: capitalizedBrand,
          expiryMonth: paymentMethod.card.exp_month,
          expiryYear: paymentMethod.card.exp_year,
          stripePaymentMethodId: paymentMethod.id
      };

      await onSuccess(paymentData);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to process payment method.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md w-full mt-4">
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm">
          <Icon name="AlertCircle" className="inline mr-2 h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 sm:col-span-1">
          <Input 
            label="Nickname (Optional)" 
            value={nickname} 
            onChange={(e) => setNickname(e.target.value)} 
            placeholder="e.g. Business Card" 
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Input 
            label="Cardholder Name" 
            value={cardHolderName} 
            onChange={(e) => setCardHolderName(e.target.value)} 
            placeholder="Name on card" 
            required 
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Icon name="CreditCard" size={16} className="text-gray-400" />
                </div>
                <div className="absolute inset-y-0 left-9 flex items-center pointer-events-none z-10">
                    <span className="text-xs font-semibold text-gray-500 uppercase">{detectedBrand !== 'Unknown' ? detectedBrand : 'Card'}</span>
                </div>
                <div id="card-number-element" className="pl-24 pr-3 py-2.5 border border-gray-300 rounded-md bg-white min-h-[42px]"></div>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                <div id="card-expiry-element" className="px-3 py-2.5 border border-gray-300 rounded-md bg-white min-h-[42px]"></div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CVC</label>
                <div id="card-cvc-element" className="px-3 py-2.5 border border-gray-300 rounded-md bg-white min-h-[42px]"></div>
            </div>
        </div>
      </div>
      
      {children}

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button variant="outline" type="button" onClick={onCancel} disabled={loading || disabled}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={!stripeReady || loading || disabled} className="flex-1 sm:flex-none">
          {loading ? (
            <><Icon name="Loader2" className="animate-spin mr-2 h-4 w-4" /> Processing...</>
          ) : buttonText}
        </Button>
      </div>
    </form>
  );
}
